import { z } from 'zod'

import {
  type BillingInfo,
  type CreateEnterpriseFromAdminRequest,
  type CreateSalesLedEnterpriseCustomBillingRequest,
  type CreateSalesLedEnterpriseRequest,
  CreateSalesLedEnterpriseRequestSkuEnum,
  type CustomBillingConfigInput,
  StripeProductOption,
} from '@/api/generated'

const Sku = CreateSalesLedEnterpriseRequestSkuEnum
type Sku = CreateSalesLedEnterpriseRequestSkuEnum

// Each entry fixes the backend plan; Enterprise entries also carry the sku (which Stripe product bills the seats).
// Hardcoded labels: these Stripe prices can't be updated once a transaction references them.
export const PLAN_OPTIONS: { value: string; label: string; plan: StripeProductOption; sku?: Sku }[] = [
  {
    value: Sku.EnterpriseDictation,
    label: 'Flow Enterprise Dictation — yearly, negotiated seat price',
    plan: StripeProductOption.FlowBusinessYearly,
    sku: Sku.EnterpriseDictation,
  },
  {
    value: Sku.EnterpriseNotetaker,
    label: 'Flow Enterprise Notetaker — yearly, negotiated seat price',
    plan: StripeProductOption.FlowBusinessYearly,
    sku: Sku.EnterpriseNotetaker,
  },
  {
    value: Sku.EnterpriseBundle,
    label: 'Flow Enterprise Bundle (Dictation + Notetaker) — yearly, negotiated seat price',
    plan: StripeProductOption.FlowBusinessYearly,
    sku: Sku.EnterpriseBundle,
  },
  {
    value: StripeProductOption.FlowBusinessYearly,
    label: 'Business Yearly — $24/seat/mo ($288/yr)',
    plan: StripeProductOption.FlowBusinessYearly,
  },
  {
    value: StripeProductOption.FlowBusinessMonthly,
    label: 'Business Monthly — $30/seat/mo',
    plan: StripeProductOption.FlowBusinessMonthly,
  },
  {
    value: StripeProductOption.FlowTeamYearly,
    label: 'Team Yearly — $10/seat/mo ($120/yr)',
    plan: StripeProductOption.FlowTeamYearly,
  },
  {
    value: StripeProductOption.FlowTeamMonthly,
    label: 'Team Monthly — $12/seat/mo',
    plan: StripeProductOption.FlowTeamMonthly,
  },
  {
    value: StripeProductOption.FlowProYearly,
    label: 'Pro Yearly — $12/seat/mo ($144/yr)',
    plan: StripeProductOption.FlowProYearly,
  },
  {
    value: StripeProductOption.FlowProMonthly,
    label: 'Pro Monthly — $15/seat/mo',
    plan: StripeProductOption.FlowProMonthly,
  },
]

const optionOf = (value: string) => PLAN_OPTIONS.find((o) => o.value === value)

// Unknown values never reach the wire (the schema refines `plan` against the table); the fallback only keeps render paths safe.
export const planOf = (value: string): StripeProductOption =>
  optionOf(value)?.plan ?? StripeProductOption.FlowBusinessYearly

export const skuOf = (value: string): Sku | undefined => optionOf(value)?.sku

export const getPlanLabel = (value: string): string => optionOf(value)?.label ?? value

export const isEnterprisePlan = (value: string): boolean => skuOf(value) !== undefined

export const billsNotetaker = (value: string): boolean => {
  const sku = skuOf(value)
  return sku === Sku.EnterpriseNotetaker || sku === Sku.EnterpriseBundle
}

export const isYearly = (plan: StripeProductOption): boolean => plan.endsWith('_YEARLY')

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)

// Renders a human-legible preview of the custom seat price the rep just typed.
// Yearly plan: input is cents/seat/year — show /yr first, then the /mo equivalent.
// Monthly plan: input is cents/seat/month — show /mo first, then the /yr equivalent.
// Returns '' when the input isn't a positive whole-cent value.
export const formatCustomSeatPricePreview = (cents: number, plan: StripeProductOption): string => {
  if (!Number.isInteger(cents) || cents < 1) return ''
  const dollars = cents / 100
  if (isYearly(plan)) {
    return `≈ ${usd(dollars)}/seat/yr (${usd(dollars / 12)}/seat/mo)`
  }
  return `≈ ${usd(dollars)}/seat/mo (${usd(dollars * 12)}/seat/yr)`
}

export const PLATFORM_FEE_OPTIONS = [
  { value: '0', label: '$0' },
  { value: '200', label: '$200/mo ($2,400/yr)' },
  { value: '250', label: '$250/mo ($3,000/yr)' },
  { value: '500', label: '$500/mo ($6,000/yr)' },
  { value: '1000', label: '$1,000/mo ($12,000/yr)' },
  { value: '1500', label: '$1,500/mo ($18,000/yr)' },
  { value: '2000', label: '$2,000/mo ($24,000/yr)' },
  { value: '3000', label: '$3,000/mo ($36,000/yr)' },
]

export const PAYMENT_TERMS_OPTIONS = [
  { value: '30', label: 'Net 30' },
  { value: '45', label: 'Net 45' },
  { value: '60', label: 'Net 60' },
]

// Mirrors `_STATE_REQUIRED_COUNTRIES` in
// aria-web/src/backend_service/schemas/api/internal.py. Most countries don't
// have a state/province subdivision; Stripe only requires it for US/CA. Keep
// this list in lockstep with the backend constant.
const STATE_REQUIRED_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA'])

export const requiresState = (country: string | undefined): boolean => {
  const code = country?.trim().toUpperCase()
  return code !== undefined && STATE_REQUIRED_COUNTRIES.has(code)
}

export const enterpriseSchema = z
  .object({
    email: z.string().email(),
    domain: z.string().min(1, 'Domain cannot be empty'),
    name: z.string().min(1, 'Enterprise name cannot be empty'),
    preserveContractedSeats: z.boolean(),
    isSalesLed: z.boolean(),
    plan: z.string().refine((v) => optionOf(v) !== undefined, { message: 'Unknown plan' }),
    seats: z.coerce.number<number>(),
    monthlyPlatformFee: z.string(),
    daysUntilDue: z.coerce.number<number>(),
    // Custom pricing toggle + amount. Amount required only when customPricing === true.
    // Currency is implicit USD (backend rejects others); see schemas/api/internal.py.
    customPricing: z.boolean(),
    customUnitPriceCents: z.string().optional(),
    // Admin user provisioning — required only when createAdminUserIfMissing is on.
    createAdminUserIfMissing: z.boolean(),
    adminFirstName: z.string().optional(),
    adminLastName: z.string().optional(),
    // Billing info — billingContact + address required when sales-led, optional otherwise.
    // billingContact overrides the Stripe customer's email so invoices route there.
    // Format check at the field level: empty / whitespace passes, anything else
    // must be a valid email. Presence is enforced in `superRefine` for sales-led.
    billingContact: z
      .string()
      .optional()
      .refine(
        (v) => {
          const trimmed = v?.trim()
          if (!trimmed) return true
          return z.string().email().safeParse(trimmed).success
        },
        { message: 'Enter a valid email address' }
      ),
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().max(2, 'Use ISO 3166-1 alpha-2 (e.g. US)').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isSalesLed) {
      if (!data.seats || data.seats < 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['seats'], message: 'At least 1 seat required' })
      }
      if (!data.daysUntilDue || data.daysUntilDue < 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['daysUntilDue'], message: 'Payment terms required' })
      }
      // Field-level refinement handles format; superRefine only enforces presence.
      if (!data.billingContact?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingContact'],
          message: 'Billing contact email is required',
        })
      }
      // line1/city/postal_code/country are universally required by Stripe. State
      // is conditionally required via `requiresState`, which mirrors the
      // backend's `_STATE_REQUIRED_COUNTRIES`. Most countries (SG, AE, HK, etc.)
      // don't have a state/province subdivision and requiring it would reject
      // otherwise-valid international payloads.
      const requiredAddressFields: Array<['line1' | 'city' | 'postalCode' | 'country', string]> = [
        ['line1', 'Address line 1 is required'],
        ['city', 'City is required'],
        ['postalCode', 'Postal code is required'],
        ['country', 'Country is required'],
      ]
      for (const [field, message] of requiredAddressFields) {
        if (!data[field]?.trim()) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message })
        }
      }
      if (requiresState(data.country) && !data.state?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['state'],
          message: 'State / region is required',
        })
      }
    }
    if (data.isSalesLed && isEnterprisePlan(data.plan) && !data.customPricing) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plan'],
        message: 'Enterprise plans are priced per deal: turn on Custom pricing and enter the seat price',
      })
    }
    if (data.isSalesLed && data.customPricing) {
      const price = Number(data.customUnitPriceCents)
      if (!data.customUnitPriceCents || !Number.isInteger(price) || price < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitPriceCents'],
          message: 'Custom unit price must be a positive whole number (cents)',
        })
      }
    }
    if (data.createAdminUserIfMissing) {
      if (!data.adminFirstName?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['adminFirstName'], message: 'First name is required' })
      }
      if (!data.adminLastName?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['adminLastName'], message: 'Last name is required' })
      }
    }
  })

export type EnterpriseFormValues = z.infer<typeof enterpriseSchema>

export const defaultFormValues: EnterpriseFormValues = {
  email: '',
  domain: '',
  name: '',
  preserveContractedSeats: true,
  isSalesLed: false,
  plan: StripeProductOption.FlowBusinessYearly,
  seats: 1,
  monthlyPlatformFee: '0',
  daysUntilDue: 30,
  customPricing: false,
  customUnitPriceCents: '',
  createAdminUserIfMissing: false,
  adminFirstName: '',
  adminLastName: '',
  billingContact: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
}

const buildCustomBillingConfig = (data: EnterpriseFormValues): CustomBillingConfigInput | undefined => {
  if (!data.customPricing) return undefined
  const price = Number(data.customUnitPriceCents)
  if (!Number.isInteger(price) || price < 1) return undefined
  // Currency omitted — backend defaults to USD (the only currently supported value).
  return { custom_unit_price_cents: price }
}

export const buildBillingInfo = (data: EnterpriseFormValues): BillingInfo | undefined => {
  const addressFields = {
    line1: data.line1?.trim() || undefined,
    line2: data.line2?.trim() || undefined,
    city: data.city?.trim() || undefined,
    state: data.state?.trim() || undefined,
    postal_code: data.postalCode?.trim() || undefined,
    country: data.country?.trim().toUpperCase() || undefined,
  }
  const hasAddress = Object.values(addressFields).some(Boolean)
  const billingContact = data.billingContact?.trim() || undefined

  if (!billingContact && !hasAddress) return undefined

  return {
    billing_contact: billingContact,
    address: hasAddress ? addressFields : undefined,
  }
}

const adminUserFields = (data: EnterpriseFormValues) =>
  data.createAdminUserIfMissing
    ? {
        create_admin_user_if_missing: true,
        admin_first_name: data.adminFirstName?.trim(),
        admin_last_name: data.adminLastName?.trim(),
      }
    : {}

export const toSalesLedPayload = (data: EnterpriseFormValues): CreateSalesLedEnterpriseRequest => {
  const billingInfo = buildBillingInfo(data)
  const sku = skuOf(data.plan)
  if (!billingInfo) {
    // Unreachable post-validation: superRefine rejects sales-led submissions that
    // lack a billing_contact or address, so by the time we reach this payload
    // converter `buildBillingInfo` must return a populated object. The throw
    // exists purely to narrow the type for the BillingInfo-required field below
    // and to fail loudly if the schema and converter ever drift apart.
    throw new Error('unreachable: buildBillingInfo returned undefined for sales-led submission')
  }
  return {
    email: data.email,
    domain: data.domain,
    enterprise_name: data.name,
    plan: planOf(data.plan),
    ...(sku ? { sku } : {}),
    seats: data.seats,
    platform_fee_monthly_rate: Number(data.monthlyPlatformFee),
    days_until_due: data.daysUntilDue,
    preserve_contracted_seats: data.preserveContractedSeats,
    billing_info: billingInfo,
    ...adminUserFields(data),
  }
}

// Superadmin-only — requires a custom_billing_config and posts to /create-sales-led-custom-billing.
export const toSalesLedCustomBillingPayload = (
  data: EnterpriseFormValues
): CreateSalesLedEnterpriseCustomBillingRequest | null => {
  const customBillingConfig = buildCustomBillingConfig(data)
  if (!customBillingConfig) return null
  return {
    ...toSalesLedPayload(data),
    custom_billing_config: customBillingConfig,
  }
}

export const toAdminPayload = (data: EnterpriseFormValues): CreateEnterpriseFromAdminRequest => ({
  admin_email: data.email,
  enterprise_domain: data.domain,
  enterprise_name: data.name,
  preserve_contracted_seats: data.preserveContractedSeats,
  billing_info: buildBillingInfo(data),
  ...adminUserFields(data),
})
